import { Response } from '../types/base';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAppStore } from '@/store/app-store';
import { generateComTfDye } from '@/app/utils/generate-dye';
import { useAdminStore } from '@/app/admin/admin-store';
import { ErrorMessageWhiteList } from '@/app/utils/constant';

const apiUrl = '/apac';

enum StatusCode {
  SuccessfulCode = '200',
  ErrorCode = '-1',
}
const headers: Readonly<Record<string, string | boolean>> = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=utf-8',
};

const fileUploadheaders: Readonly<Record<string, string | boolean>> = {
  'Content-Type': 'multipart/form-data',
};

const injectToken = (config: AxiosRequestConfig): any => {
  if (typeof window !== 'undefined') {
    try {
      const country = localStorage.getItem('country') || 'cn';
      config.headers = {
        country,
        'com-tf-dye': generateComTfDye(),
        admin_language: useAdminStore.getState()?.adminLanguage,
      };
      return config;
    } catch (error) {
      throw new Error('Inject Token fails');
    }
  } else {
    return config;
  }
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: apiUrl,
      headers,
    });
    http.interceptors.request.use(injectToken, (error: any) => Promise.reject(error));

    http.interceptors.response.use(
      (response) => {
        if (response.data.code === StatusCode.SuccessfulCode) {
          return response.data;
        }
        return this.handleError(response);
      },
      (error) => this.handleHttpError(error.response)
    );

    this.instance = http;
    return http;
  }

  request<T = any>(config: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.request(config);
  }

  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.get(url, config);
  }

  post<T = any>(url: string, data, config?: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.post(url, data, config);
  }

  put<T>(url: string, data?: T, config?: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.put(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.delete(url, config);
  }

  fileUpload<T = any>(url: string, data, config?: AxiosRequestConfig): Promise<Response<T>> {
    return this.http.post(url, data, {
      ...config,
      headers: fileUploadheaders,
    });
  }

  private handleError(error) {
    const errorData = error?.data;
    const code = error?.data?.code;
    const errorId = error?.headers?.['com-tf-dye'] || '';
    const requestUrl = error?.request?.responseURL || '';

    switch (code) {
      case StatusCode.ErrorCode:
        if (ErrorMessageWhiteList.some((url) => requestUrl.includes(url))) {
          return errorData;
        }
        useAppStore.getState().addCommonError('common.system.error', { errorId });
        return Promise.reject(errorData);
      default:
        return errorData;
    }
  }

  private handleHttpError(error: any) {
    const status = error?.status;
    switch (status) {
      case 500: {
        break;
      }
      case 404: {
        break;
      }
      case 401: {
        window.location.href = `/apac/user-expired?returnUrl=${window.location.href}`;
        throw new Error('Unauthorized');
      }
      case 403: {
        useAppStore.getState().addCommonError('not.access.error');
        throw new Error('Forbidden');
      }
    }
    const errorId = error?.headers?.['com-tf-dye'] || '';
    useAppStore.getState().addCommonError('common.system.error', { errorId });
    return Promise.reject(error);
  }
}

export const http = new Http();
